import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Star, Trash2, Loader2, GitMerge, X, Check, Film, Search } from "lucide-react";
import { getRoster, setGlobalFollow, renamePerson, deletePerson, mergePeople, getRosterSuggestions, undoMerge, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import PersonClips from "./PersonClips";

export default function RosterView() {
  const [roster, setRoster] = useState({ people: [], follow_name: null });
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [mergeMode, setMergeMode] = useState(false);
  const [selected, setSelected] = useState([]); // person ids
  const [keepId, setKeepId] = useState(null);
  const [merging, setMerging] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [detailPerson, setDetailPerson] = useState(null);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("shows"); // shows | recent | name

  const load = async () => {
    try {
      const g = await getRoster();
      setRoster(g);
      setDrafts(Object.fromEntries((g.people || []).map((p) => [p.id, p.name])));
    } catch {
      toast.error("Could not load roster");
    } finally {
      setLoading(false);
    }
    try {
      const s = await getRosterSuggestions();
      setSuggestions(s.pairs || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { load(); }, []);

  const saveName = async (p) => {
    const name = (drafts[p.id] || "").trim();
    if (!name || name === p.name) return;
    try {
      const g = await renamePerson(p.id, name);
      setRoster(g);
      toast.success("Renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const toggleFollow = async (p) => {
    const isFollow = (roster.follow_name || "").toLowerCase() === p.name.toLowerCase();
    try {
      const g = await setGlobalFollow(isFollow ? "" : p.name);
      setRoster((r) => ({ ...r, follow_name: g.follow_name }));
      toast.success(isFollow ? `Stopped auto-following ${p.name}` : `Now auto-following ${p.name} in every show`);
    } catch {
      toast.error("Could not update default");
    }
  };

  const remove = async (p) => {
    try {
      const g = await deletePerson(p.id);
      setRoster(g);
      setDrafts(Object.fromEntries((g.people || []).map((x) => [x.id, x.name])));
      toast.success(`Removed ${p.name}`);
    } catch {
      toast.error("Could not remove");
    }
  };

  const people = roster.people || [];

  const exitMerge = () => { setMergeMode(false); setSelected([]); setKeepId(null); };

  const reviewPair = (pair) => {
    setDismissed(true);
    setMergeMode(true);
    setSelected([pair.a.id, pair.b.id]);
    const best = (pair.a.shows ?? 0) >= (pair.b.shows ?? 0) ? pair.a : pair.b;
    setKeepId(best.id);
  };

  const toggleSelect = (p) => {
    setSelected((prev) => {
      const next = prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id];
      // default keeper = selected person with the most shows
      const pool = people.filter((x) => next.includes(x.id));
      if (pool.length) {
        const best = pool.reduce((a, b) => ((b.shows ?? 0) > (a.shows ?? 0) ? b : a));
        setKeepId((k) => (next.includes(k) ? k : best.id));
      } else {
        setKeepId(null);
      }
      return next;
    });
  };

  const doMerge = async () => {
    if (selected.length < 2 || !keepId) return;
    const keeper = people.find((p) => p.id === keepId);
    const losers = selected.filter((id) => id !== keepId);
    setMerging(true);
    try {
      let g = roster;
      const tokens = [];
      for (const merge_id of losers) {
        g = await mergePeople(keepId, merge_id);
        if (g.undo) tokens.push(g.undo);
      }
      setRoster(g);
      setDrafts(Object.fromEntries((g.people || []).map((x) => [x.id, x.name])));
      exitMerge();
      try { const s = await getRosterSuggestions(); setSuggestions(s.pairs || []); } catch {}
      toast.success(`Merged ${losers.length + 1} into ${keeper?.name}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const back = await undoMerge(tokens);
              setRoster(back);
              setDrafts(Object.fromEntries((back.people || []).map((x) => [x.id, x.name])));
              try { const s = await getRosterSuggestions(); setSuggestions(s.pairs || []); } catch {}
              toast.success("Merge undone");
            } catch {
              toast.error("Could not undo");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const keeper = people.find((p) => p.id === keepId);

  const sortPeople = (list) => {
    const arr = [...list];
    if (sortBy === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "recent") arr.sort((a, b) => (b.added_at || "").localeCompare(a.added_at || ""));
    else arr.sort((a, b) => (b.shows ?? 0) - (a.shows ?? 0));
    return arr;
  };

  if (detailPerson) {
    return <PersonClips person={detailPerson} followName={roster.follow_name} onBack={() => { setDetailPerson(null); load(); }} />;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-3xl pb-28" data-testid="roster-view">
      <div className="flex items-center gap-3 border-b border-border pb-6">
        <div className="grid h-11 w-11 place-items-center bg-primary">
          <Users className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-3xl tracking-widest text-white">ROSTER</h2>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-accent">Everyone you've tagged, in one place</p>
        </div>
        {people.length >= 2 && (
          mergeMode ? (
            <button
              data-testid="roster-merge-cancel"
              onClick={exitMerge}
              className="flex shrink-0 items-center gap-1.5 border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-white"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          ) : (
            <button
              data-testid="roster-merge-toggle"
              onClick={() => setMergeMode(true)}
              className="flex shrink-0 items-center gap-1.5 border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <GitMerge className="h-3.5 w-3.5" /> Merge duplicates
            </button>
          )
        )}
      </div>

      <p className="mt-5 font-body text-sm text-muted-foreground">
        {mergeMode ? (
          <>Select two or more rows that are the <span className="text-accent">same person</span>. Choose which name to keep, then merge — every show tagged with the others is re-tagged to the kept name.</>
        ) : (
          <>Name a face once inside any clip and it lands here. Star a person to make them your <span className="text-accent">default follow</span> —
          every new show will center the vertical crop on them automatically (when they appear).</>
        )}
      </p>

      {!mergeMode && !dismissed && suggestions.length > 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 border border-accent/50 bg-accent/[0.06] p-4"
          data-testid="roster-suggestions"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center bg-accent/20 text-accent">
              <GitMerge className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="font-mono text-[11px] uppercase tracking-widest text-accent">
                {suggestions.length} possible duplicate{suggestions.length === 1 ? "" : "s"} found
              </p>
              <p className="mt-0.5 font-body text-sm text-muted-foreground">
                These faces sit in nearly the same spot — they might be the same person tagged twice.
              </p>
              <div className="mt-3 space-y-2">
                {suggestions.map((pair) => (
                  <div
                    key={`${pair.a.id}-${pair.b.id}`}
                    data-testid={`roster-suggestion-${pair.a.id}-${pair.b.id}`}
                    className="flex items-center gap-3 border border-border bg-card px-3 py-2"
                  >
                    <div className="flex -space-x-2">
                      {[pair.a, pair.b].map((x) =>
                        x.avatar ? (
                          <img key={x.id} src={mediaUrl(x.avatar)} alt={x.name} className="h-8 w-8 rounded-full border border-card object-cover" />
                        ) : (
                          <div key={x.id} className="grid h-8 w-8 place-items-center rounded-full border border-card bg-secondary font-display text-xs text-muted-foreground">
                            {(x.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )
                      )}
                    </div>
                    <span className="flex-1 truncate font-body text-sm text-white">
                      <span className="text-accent">{pair.a.name}</span> &amp; <span className="text-accent">{pair.b.name}</span>
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                        {pair.reason === "look-alike" ? "look alike" : "same spot"}
                      </span>
                    </span>
                    <button
                      data-testid={`roster-review-${pair.a.id}-${pair.b.id}`}
                      onClick={() => reviewPair(pair)}
                      className="shrink-0 bg-accent px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-black transition-opacity hover:opacity-90"
                    >
                      Review &amp; merge
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button
              data-testid="roster-suggestions-dismiss"
              onClick={() => setDismissed(true)}
              className="shrink-0 text-muted-foreground transition-colors hover:text-white"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}

      {!loading && !mergeMode && people.length > 4 && (
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="roster-toolbar">
          <div className="relative min-w-[200px] flex-1" data-testid="roster-search-wrap">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="roster-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the cast…"
              className="w-full border border-border bg-background py-2.5 pl-10 pr-3 font-body text-sm text-white outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="flex items-center border border-border" data-testid="roster-sort">
            {[{ v: "shows", l: "Most clips" }, { v: "recent", l: "Recent" }, { v: "name", l: "A–Z" }].map((s) => (
              <button
                key={s.v}
                data-testid={`roster-sort-${s.v}`}
                onClick={() => setSortBy(s.v)}
                className={`px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  sortBy === s.v ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : people.length === 0 ? (
        <div className="mt-10 border border-dashed border-border p-10 text-center" data-testid="roster-empty">
          <p className="font-mono text-sm text-muted-foreground">
            No faces tagged yet. Open a video clip → Style → "Name the faces" to start your roster.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-2">
          {(mergeMode ? people : sortPeople(people.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase())))).length === 0 ? (
            <p className="py-10 text-center font-mono text-sm text-muted-foreground" data-testid="roster-no-matches">
              No one matches "{query}".
            </p>
          ) : (mergeMode ? people : sortPeople(people.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase())))).map((p) => {
            const isFollow = (roster.follow_name || "").toLowerCase() === p.name.toLowerCase();
            const isSelected = selected.includes(p.id);
            const isKeeper = keepId === p.id;
            return (
              <div
                key={p.id}
                data-testid={`roster-row-${p.id}`}
                onClick={mergeMode ? () => toggleSelect(p) : undefined}
                className={`flex items-center gap-3 border px-4 py-3 transition-colors ${
                  mergeMode
                    ? `cursor-pointer ${isSelected ? "border-accent bg-accent/[0.08]" : "border-border bg-card hover:border-muted-foreground"}`
                    : isFollow ? "border-accent bg-accent/[0.06]" : "border-border bg-card"
                }`}
              >
                {mergeMode ? (
                  <div
                    data-testid={`roster-select-${p.id}`}
                    className={`grid h-5 w-5 shrink-0 place-items-center border transition-colors ${isSelected ? "border-accent bg-accent" : "border-muted-foreground"}`}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                  </div>
                ) : (
                  <button
                    data-testid={`roster-star-${p.id}`}
                    onClick={() => toggleFollow(p)}
                    title={isFollow ? "Default follow — click to unset" : "Set as default follow for all shows"}
                    className={`shrink-0 transition-colors ${isFollow ? "text-accent" : "text-muted-foreground hover:text-white"}`}
                  >
                    <Star className="h-5 w-5" fill={isFollow ? "#CCFF00" : "none"} />
                  </button>
                )}
                {p.avatar ? (
                  <img
                    data-testid={`roster-avatar-${p.id}`}
                    src={mediaUrl(p.avatar)}
                    alt={p.name}
                    className="h-10 w-10 shrink-0 rounded-full border border-border object-cover"
                  />
                ) : (
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border bg-secondary font-display text-sm text-muted-foreground">
                    {(p.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                {mergeMode ? (
                  <span className="min-w-0 flex-1 truncate font-body text-base text-white">{p.name}</span>
                ) : (
                  <input
                    data-testid={`roster-name-${p.id}`}
                    value={drafts[p.id] ?? ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    onBlur={() => saveName(p)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="min-w-0 flex-1 bg-transparent font-body text-base text-white outline-none focus:text-accent"
                  />
                )}
                {mergeMode && isSelected && (
                  <button
                    data-testid={`roster-keep-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); setKeepId(p.id); }}
                    className={`shrink-0 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest transition-colors ${
                      isKeeper ? "bg-accent text-black" : "border border-muted-foreground text-muted-foreground hover:text-white"
                    }`}
                  >
                    {isKeeper ? "Keep this ✓" : "Keep"}
                  </button>
                )}
                {!mergeMode && isFollow && (
                  <span className="shrink-0 bg-accent px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-black">
                    Default
                  </span>
                )}
                {!mergeMode ? (
                  <button
                    data-testid={`roster-viewclips-${p.id}`}
                    onClick={() => setDetailPerson(p)}
                    title={`View every clip featuring ${p.name}`}
                    className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-accent"
                  >
                    <Film className="h-3.5 w-3.5" />
                    {p.shows ?? 0} show{(p.shows ?? 0) === 1 ? "" : "s"}
                  </button>
                ) : (
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {p.shows ?? 0} show{(p.shows ?? 0) === 1 ? "" : "s"}
                  </span>
                )}
                {!mergeMode && (
                  <button
                    data-testid={`roster-delete-${p.id}`}
                    onClick={() => remove(p)}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
                    title="Remove from roster"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {mergeMode && selected.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            className="fixed inset-x-0 bottom-0 z-30 border-t border-accent bg-card/95 backdrop-blur"
            data-testid="roster-merge-bar"
          >
            <div className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-4">
              <div className="flex shrink-0 items-center gap-2" data-testid="roster-merge-preview">
                {people.filter((p) => selected.includes(p.id)).map((p) => {
                  const isKeeper = p.id === keepId;
                  return (
                    <div key={p.id} className="flex flex-col items-center gap-1">
                      {p.avatar ? (
                        <img
                          src={mediaUrl(p.avatar)}
                          alt={p.name}
                          className={`h-12 w-12 rounded-full object-cover transition-all ${isKeeper ? "ring-2 ring-accent ring-offset-2 ring-offset-card" : "opacity-60"}`}
                        />
                      ) : (
                        <div className={`grid h-12 w-12 place-items-center rounded-full bg-secondary font-display text-base text-muted-foreground transition-all ${isKeeper ? "ring-2 ring-accent ring-offset-2 ring-offset-card" : "opacity-60"}`}>
                          {(p.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className={`font-mono text-[8px] uppercase tracking-widest ${isKeeper ? "text-accent" : "text-muted-foreground"}`}>
                        {isKeeper ? "keep" : p.name.slice(0, 8)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex-1">
                <p className="font-mono text-[11px] uppercase tracking-widest text-accent">
                  Merging {selected.length} people
                </p>
                <p className="font-body text-sm text-white">
                  Keeping <span className="font-bold text-accent">{keeper?.name}</span> — the rest fold into this name
                </p>
              </div>
              <button
                data-testid="roster-merge-confirm"
                onClick={doMerge}
                disabled={merging || !keepId}
                className="flex items-center gap-2 bg-accent px-5 py-2.5 font-mono text-[12px] font-bold uppercase tracking-widest text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
                Merge into one
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
